import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { TSetPriorityMode } from '../Types';
import ModeItemRow from './ModeItemRow';
import { reorderModes, updateModeField } from '../ModeTransformUtils';

type IndividualModesViewProps = {
    modes: TSetPriorityMode[];
    onModesChange: (modes: TSetPriorityMode[]) => void;
    onSettingsClick: (modeKey: string) => void;
    disabled?: boolean;
    id?: string;
};

export const IndividualModesView = ({
    modes,
    onModesChange,
    onSettingsClick,
    disabled = false,
    id = 'individual-modes-view'
}: IndividualModesViewProps) => {
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const reorderedModes = reorderModes(
            modes,
            result.source.index,
            result.destination.index
        );
        onModesChange(reorderedModes);
    };

    const handleModeToggle = (index: number, checked: boolean) => {
        const updatedModes = updateModeField(modes, index, 'active', checked);
        onModesChange(updatedModes);
    };

    const handleTitleChange = (index: number, value: string) => {
        const updatedModes = updateModeField(modes, index, 'title', value);
        onModesChange(updatedModes);
    };

    const handleMinProductChange = (index: number, value: number) => {
        const updatedModes = updateModeField(modes, index, 'minNumProducts', value);
        onModesChange(updatedModes);
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-2" data-testid={id}>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="individual-modes-droppable">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="tw-flex tw-flex-col tw-gap-2"
                        >
                            {modes.map((mode, index) => (
                                <Draggable
                                    key={mode.mode}
                                    draggableId={`individual-${mode.mode}`}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <ModeItemRow
                                            mode={mode}
                                            index={index}
                                            variant="individual"
                                            onToggle={(checked) =>
                                                handleModeToggle(index, checked)
                                            }
                                            onTitleChange={(value) =>
                                                handleTitleChange(index, value)
                                            }
                                            onMinProductChange={(value) =>
                                                handleMinProductChange(index, value)
                                            }
                                            onSettingsClick={() => onSettingsClick(mode.mode)}
                                            disabled={disabled && mode.mode === 'expert_curated_coseller'}
                                            provided={provided}
                                            snapshot={snapshot}
                                            id={`${id}-mode`}
                                        />
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

export default IndividualModesView;